import { useEffect, useRef, useState } from 'react';
import { SortBy, sortable } from 'tea-component/lib/table/addons';
import pLimit from 'p-limit';
import { dealwithInfluenceTableData } from '../utils';
import { arrayToObj } from '@/pages/opinion/utils';
import { getInfluencerGenreCreatorList } from '@op/services';
import { dealwithSortItem, genreCreatorListDataKeys } from '../constants';

// 默认排序指标
const tableDefaultSortKey = 'followers';

// 默认的pageSize
const defaultPageSize = 20;

/**
 * table数据的所有相关内容
 * @param params
 * @returns
 */
export default function useInfluencerData({ params, tableKey }: any) {
  const [keyword, setKeyword] = useState('');
  const [tableRecords, setTableRecord] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [apiError, setApiError] = useState(false);
  const [pageInfo, setPageInfo] = useState({
    page_size: defaultPageSize,
    page: 1,
  });
  const [sort, setSort] = useState<SortBy<any>>({
    by: tableDefaultSortKey,
    order: 'desc',
  });
  const [linkTime, setLinkTime] = useState({
    new: { end_time: '', start_time: '' },
    old: { end_time: '', start_time: '' },
  });

  const getTableListData = (otherParams: any) => {
    if (Object.keys(params || {}).length > 0) {
      setLoading(true);
      setApiError(false);
      setTableRecord([]);

      // streaming取所有数据
      getInfluencerGenreCreatorList({
        ...params,
        ...otherParams,
        search_keyword: keyword,
      })
        .then((resultData: any) => {
          const { list, old_list, total, max_item, old_plus_list, link_time } =
            resultData || {};
          const localTableRecord =
            dealwithInfluenceTableData({
              new_list: list,
              old_list,
              old_plus_list,
              max_item,
              key: tableKey,
              valueKeys: genreCreatorListDataKeys,
            }) || [];
          setTableRecord(localTableRecord);
          setLinkTime(arrayToObj(link_time, 'key'));
          setTotal(total);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const addList = (list?: Array<Array<any>>) => {
    const result: Array<any> = [];
    const merge = (arr: any) => {
      for (let index = 0; index < arr.length; index++) {
        result[index] = (result[index] || []).concat(arr[index] || []);
      }
    };
    merge(list);
    return (newList?: Array<Array<any>>) => {
      if (newList) {
        merge(newList);
      } else {
        return result;
      }
    };
  };

  const getAllTableList = async (otherParams: any) => {
    setLoading(true);
    setApiError(false);
    const onePageSize = 5000;
    // 先做一个请求获取total
    const newParams = dealwithSortItem({
      ...params,
      ...otherParams,
      search_keyword: keyword,
      page: 1,
      page_size: onePageSize,
      sort_item: tableDefaultSortKey,
      sort_model: 'desc',
    }, true);
    
    const data = await getInfluencerGenreCreatorList(newParams);

    const {
      list,
      old_list,
      total,
      user_infos,
      old_plus_list,
      max_item,
      link_time,
    } = data || {
      total: 0,
    };
    const addListFn = addList([list, old_list, user_infos, old_plus_list]);
    const requestTotal = total - onePageSize;
    if (requestTotal > 0) {
      const page = Math.ceil(requestTotal / onePageSize) + 1;
      const curPLimit = pLimit(2);
      const requestAll: Array<Promise<any>> = [];
      for (let index = 2; index <= page; index++) {
        requestAll.push(
          curPLimit(() =>
            getInfluencerGenreCreatorList({ ...newParams, page: index }),
          ),
        );
      }
      const resultAll = await Promise.all(requestAll);

      resultAll?.some((item) => {
        if (item?.code === 0) {
          const { list, old_list, user_infos, old_plus_list } = item?.data || {};
          addListFn([list, old_list, user_infos, old_plus_list]);
        } else {
          setApiError(true);
          return true;
        }
      });
    }
    const [allNewList, allOldList, oldPlusList] = addListFn() || [];
    const localTableRecord =
      dealwithInfluenceTableData({
        new_list: allNewList,
        old_list: allOldList,
        old_plus_list: oldPlusList,
        max_item,
        key: tableKey,
        valueKeys: genreCreatorListDataKeys,
      }) || [];

    localTableRecord?.sort(
      sortable.comparer([{ by: 'followers_grow', order: 'desc' }]),
    );

    setTableRecord(localTableRecord);
    setLinkTime(arrayToObj(link_time, 'key'));
    setTotal(total);
    setLoading(false);
  };

  const countStartEnd = (
    localPageInfo = pageInfo,
    tableList = tableRecords,
    sortInfo = sort,
  ) => {
    const localSort =
      sortInfo.by === 'followers_grow_more_5k'
        ? { by: 'followers_grow', order: sortInfo.order }
        : sortInfo;
    tableList?.sort(sortable.comparer([localSort])) || [];

    const { page, page_size } = localPageInfo;
    const start = (page - 1) * page_size;
    const end = page * page_size;
    const list = tableList.slice(start, end);
    if (!list || list.length === 0) {
      return [];
    }
    return list;
  };

  // 是否发起请求
  const isRequest = useRef(false);
  useEffect(() => {
    if (isRequest.current) {
      const paramsInfo = {
        ...pageInfo,
        sort_item: sort.by,
        sort_model: sort.order,
      };
      getTableListData(paramsInfo);
    }
  }, [pageInfo]);

  const changePageInfo = (
    pageIndex = 1,
    pageSize: number = defaultPageSize,
    localSort = sort,
  ) => {
    const isFollowersGrow = [
      'followers_grow_more_5k',
      'followers_grow',
    ].includes(localSort.by);

    // 直播和增长量排序都切换页面不需要请求数据
    isRequest.current = !isFollowersGrow;

    const localPageInfo = { page: pageIndex, page_size: pageSize };

    setPageInfo(localPageInfo);
  };

  const changeSortInfo = (localSort: SortBy<any>) => {
    // 随便那个不同就代表了排序变化
    if (sort.by !== localSort.by || sort.order !== localSort.order) {
      setSort(localSort);
      const isFollowersGrow = [
        'followers_grow_more_5k',
        'followers_grow',
      ].includes(localSort.by);
      changePageInfo(1, pageInfo.page_size, localSort);

      if (isFollowersGrow) {
        // 排序字段变了，需要重新请求
        if (localSort.by !== sort.by) {
          const paramsInfo =
            localSort.by === 'followers_grow_more_5k'
              ? {followers: [{start_followers: 5000, end_followers: 500000000}]}
              : {};
          getAllTableList(paramsInfo);
        } else if (localSort.order !== sort.order) {
          // 排序类型变了【倒序和正序】，需要请求keyword和切换有六个元素，但是不请求数据
          const list = countStartEnd(
            { page: 1, page_size: pageInfo.page_size },
            tableRecords,
            localSort,
          );
        }
      }
    }
  };

  useEffect(() => {
    isRequest.current = false;
    setSort({ by: tableDefaultSortKey, order: 'desc' });
    setPageInfo({ page: 1, page_size: pageInfo.page_size });
    getTableListData({
      page: 1,
      page_size: pageInfo.page_size,
      sort_item: tableDefaultSortKey,
      sort_model: 'desc',
    });
  }, [params, keyword]);

  // 排序联动能力
  // useEffect(() => {
  //   if (metric !== 'kolValue') {
  //     changeSortInfo({ order: 'desc', by: metric });
  //   }
  // }, [metric]);

  const getTableRecord = () => {
    return ['followers_grow_more_5k', 'followers_grow'].includes(sort.by)
      ? countStartEnd(pageInfo, tableRecords)
      : tableRecords;
  };

  return {
    sort,
    changeSortInfo,
    linkTime,
    total,
    apiError,
    pageInfo,
    changePageInfo,
    getTableRecord,
    keyword,
    setKeyword,
    loading,
  };
}
